import { useEffect, useState } from 'react';
import { Timer, Vote } from 'lucide-react';
import type { Unidad } from '@condo/contracts';
import { Skeleton } from '@/components/ui/skeleton';
import { useAsamblea, useMisVotos } from './api';
import { MateriaVotoCard } from './materia-voto-card';

// ─────────────────────────────────────────────────────────────────────────────
// Countdown hook
// ─────────────────────────────────────────────────────────────────────────────

function useCountdown(hasta: string) {
  const [remaining, setRemaining] = useState(() =>
    Math.max(0, new Date(hasta).getTime() - Date.now()),
  );

  useEffect(() => {
    const id = setInterval(() => {
      setRemaining(Math.max(0, new Date(hasta).getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(id);
  }, [hasta]);

  const totalSeconds = Math.floor(remaining / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const expired = remaining === 0;

  return { hours, minutes, seconds, expired };
}

// ─────────────────────────────────────────────────────────────────────────────
// Countdown display
// ─────────────────────────────────────────────────────────────────────────────

function CountdownBanner({ hasta }: { hasta: string }) {
  const { hours, minutes, seconds, expired } = useCountdown(hasta);

  if (expired) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-[var(--color-danger)] bg-[var(--color-danger)]/5 px-4 py-3">
        <Timer className="h-5 w-5 text-[var(--color-danger)] shrink-0" />
        <p className="text-sm font-semibold text-[var(--color-danger)]">
          El plazo de votacion ha cerrado.
        </p>
      </div>
    );
  }

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="flex items-center gap-3 rounded-md border border-[var(--color-accent)] bg-[var(--color-paper-dim)] px-4 py-3">
      <Timer className="h-5 w-5 text-[var(--color-accent)] shrink-0" />
      <div>
        <p className="eyebrow text-[0.6rem]">Tiempo restante para votar</p>
        <p className="font-display-tight mt-0.5 text-2xl font-bold tabular-nums tracking-tight">
          {pad(hours)}:{pad(minutes)}:{pad(seconds)}
        </p>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MesaVotacion
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  condoId: string;
  asambleaId: string;
  /**
   * Unidades que pertenecen al propietario actual.
   * El componente padre las resuelve (via useUnidades + residente lookup
   * o, en modo degradado, el llamador pasa todas las unidades del condo
   * y el backend rechaza con NoEsPropietarioError si se vota por otra).
   */
  unidadesPropias: Unidad[];
}

/**
 * Mesa de votacion en vivo para propietarios.
 * Solo visible cuando asamblea.estado === 'en_votacion'.
 */
export function MesaVotacion({ condoId, asambleaId, unidadesPropias }: Props) {
  const { data: asamblea, isLoading: loadingAsamblea } = useAsamblea(condoId, asambleaId);
  const { data: misVotosData, isLoading: loadingVotos } = useMisVotos(condoId, asambleaId);

  if (loadingAsamblea || loadingVotos) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!asamblea) return null;

  if (asamblea.estado !== 'en_votacion') {
    return (
      <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper-dim)] p-6 text-center">
        <Vote className="mx-auto h-8 w-8 text-[var(--color-ink-muted)]" />
        <p className="mt-3 text-sm text-[var(--color-ink-muted)]">
          La votacion no esta abierta en este momento.
        </p>
      </div>
    );
  }

  const misVotos = misVotosData?.items ?? [];

  // Alicuota total del propietario
  const alicuotaTotal = unidadesPropias.reduce((acc, u) => acc + u.alicuota, 0);

  // Materias que requieren votacion (no informativas)
  const materiasVotables = asamblea.materias.filter(
    (m) => m.categoria !== 'informativa',
  );
  const materiasInformativas = asamblea.materias.filter(
    (m) => m.categoria === 'informativa',
  );

  return (
    <div className="space-y-6">
      {/* Countdown */}
      <CountdownBanner hasta={asamblea.plazoVotacionHasta} />

      {/* Resumen de participacion del propietario */}
      <div className="rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] px-4 py-3">
        <p className="eyebrow text-[0.6rem]">Tu participacion</p>
        <div className="mt-1.5 flex flex-wrap gap-4">
          <div>
            <span className="text-xs text-[var(--color-ink-muted)]">Unidades: </span>
            <span className="text-sm font-semibold">
              {unidadesPropias.length === 0
                ? 'Sin unidades asignadas'
                : unidadesPropias.map((u) => u.numero).join(', ')}
            </span>
          </div>
          <div>
            <span className="text-xs text-[var(--color-ink-muted)]">Alicuota total: </span>
            <span className="text-sm font-semibold">
              {(alicuotaTotal * 100).toFixed(2)}%
            </span>
          </div>
        </div>
      </div>

      {/* Materias votables */}
      {materiasVotables.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-display text-lg font-semibold">Materias para votar</h3>
          {materiasVotables.map((materia) => (
            <MateriaVotoCard
              key={materia.id}
              condoId={condoId}
              asambleaId={asambleaId}
              materia={materia}
              unidades={unidadesPropias}
              misVotos={misVotos}
            />
          ))}
        </div>
      )}

      {/* Materias informativas */}
      {materiasInformativas.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-display text-lg font-semibold">Puntos informativos</h3>
          {materiasInformativas.map((materia) => (
            <MateriaVotoCard
              key={materia.id}
              condoId={condoId}
              asambleaId={asambleaId}
              materia={materia}
              unidades={unidadesPropias}
              misVotos={misVotos}
            />
          ))}
        </div>
      )}

      {asamblea.materias.length === 0 && (
        <p className="text-sm text-[var(--color-ink-muted)]">
          Sin materias en el orden del dia.
        </p>
      )}
    </div>
  );
}
